import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";

import { i18n } from "./plugins/i18n";

import { publicationCategoryResource, ordersResource, paymentResource, authorizationResource } from "./api/resources";

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  key: "publication-orders-page",
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    categories: [],
    cart: [],
    user: {},
    publicationIdToAdd: null,
    languageIdToAdd: null,
    loading: false,
    payment: {},
  },
  mutations: {
    setCategories(state, payload) {
      state.categories = payload;
    },
    setCart(state, payload) {
      state.cart = payload;
    },
    setUser(state, payload) {
      state.user = payload;
    },
    setPublicationIdToAdd(state, payload) {
      state.publicationIdToAdd = payload;
    },
    setLanguageIdToAdd(state, payload) {
      state.languageIdToAdd = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    },
    setPayment(state, payload) {
      state.payment = payload;
    },
  },
  actions: {
    clearOrder({ commit }) {
      commit("setUser", {});
      commit("setCart", []);
    },

    async getCategories({ commit }) {
      const categories = await publicationCategoryResource.get("", {
        headers: {
          "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
        },
      });
      commit("setCategories", categories.data.results);
    },

    async submitReservation({ state }) {
      await ordersResource.post(
        "",
        {
          ...state.user,
          payment: state.payment,
          publications: state.cart,
        },
        {
          headers: {
            "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
    },

    addToCart({ state, getters, commit }) {
      let id = state.publicationIdToAdd;
      if (getters.publicationToAdd.languages.length > 0) {
        id += `-${state.languageIdToAdd}`;
      }

      const existingCartIndex = state.cart.findIndex((element) => element.internalId === id);

      if (existingCartIndex >= 0) {
        state.cart[existingCartIndex].amount++;
        commit("setCart", state.cart);
      } else {
        // eslint-disable-next-line no-console
        state.cart.push({
          ...getters.publicationToAdd,
          internalId: id,
          amount: 1,
          language: getters.publicationToAdd.languages.filter((language) => language.id === state.languageIdToAdd)[0],
        });
        commit("setCart", state.cart);
      }
    },

    toggleLoading({ commit }, payload) {
      commit("setLoading", payload);
    },

    async charge({ state }, source) {
      return (
        await paymentResource.post(
          `/charge-publications-order/${source}`,
          {
            cart: state.cart,
          },
          {
            headers: {
              "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
            },
          }
        )
      ).data;
    },

    async authorize({ state }) {
      try {
        const payment = (
          await authorizationResource.post(
            "",
            {
              cart: state.cart,
            },
            {
              headers: {
                "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
              },
            }
          )
        ).data;

        return payment;
      } catch (err) {
        return {
          err: true,
        };
      }
    },

    authorizeBankTransfer({ commit }) {
      commit("setPayment", {
        method: "banktransfer",
      });
    },

    decreaseCartAmount({ state }, index) {
      const cartData = state.cart[index];
      if (cartData.amount > 1) {
        --cartData.amount;
      } else {
        state.cart.splice(index, 1);
      }
    },
  },
  getters: {
    categories: (state) => state.categories,
    groupedPublications: (state) => {
      const publications = [];
      let firstEntry = true;
      for (const category of state.categories) {
        if (!category.publications.find((publication) => !publication.outOfStock && publication.price > 0)) {
          continue;
        }
        if (!firstEntry) {
          publications.push({ divider: true });
        }
        firstEntry = false;
        publications.push({
          header: i18n.locale === "en" ? category.title_en : category.title,
        });
        category.publications.forEach((publication) => {
          if (publication.outOfStock) {
            return;
          }
          publications.push({
            ...publication,
            title: i18n.locale === "en" ? publication.title_en || publication.title : publication.title,
            publisher: i18n.locale === "en" ? publication.publisher_en || publication.publisher : publication.publisher,
          });
        });
      }
      return publications;
    },

    publicationToAdd: (state, getters) => {
      if (!state.publicationIdToAdd) {
        return null;
      }
      const publication = getters.groupedPublications.find(
        (publication) => publication.id === state.publicationIdToAdd
      );

      // eslint-disable-next-line no-console
      if (!publication) {
        return null;
      }

      return {
        ...publication,
        languages: publication.languages.map((language) => {
          return {
            ...language,
            title: i18n.locale === "en" ? language.title_en || language.title : language.title,
          };
        }),
      };
    },

    cartSum: (state) => {
      return state.cart.map((element) => element.amount * element.price).reduce((left, right) => left + right, 0);
    },

    allowAddToCart: (state, getters) => {
      if (!getters.publicationToAdd) {
        return false;
      }
      if (getters.publicationToAdd.languages.length === 0) {
        return true;
      }

      return state.languageIdToAdd || getters.publicationToAdd.languages.length === 0;
    },
  },

  plugins: [vuexLocal.plugin],
});
